import toast from "react-hot-toast";
import axiosInstance from "helpers/api";
import { formatErrorForReact } from "helpers/utils";
import Logo from "components/common/Logo";
import { useCookies } from "react-cookie";
import Button from "components/base/Button";
import { loadStripe } from "@stripe/stripe-js";
import { Fragment, useEffect, useState } from "react";
import configService from "services/configService";
import { useForm, SubmitHandler } from "react-hook-form";
import { Col, Form, Row, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { faEye, faEyeSlash, faBuilding, faCrown } from "@fortawesome/free-solid-svg-icons";

type BrokerInputs = {
  phone: string;
  email: string;
  password: string;
  last_name: string;
  first_name: string;
  confirm_password: string;
  brokerage_name: string;
  brokerage_license: string;
  office_address: string;
  mls_number?: string;
};

const BrokerSignUpForm = () => {
  const [cookie, setCookie] = useCookies(["isLoggedInKIQ", "stripeIdpKey"]);
  
  // Initialize Stripe with secure configuration
  const [stripePromise, setStripePromise] = useState<Promise<any> | null>(null);

  useEffect(() => {
    const initializeStripe = async () => {
      try {
        const stripeConfig = await configService.getStripeConfig();
        const stripe = loadStripe(stripeConfig.publishableKey);
        setStripePromise(stripe);
        console.log('✅ [BrokerSignUp] Initialized Stripe with secure publishable key');
      } catch (error) {
        console.warn('⚠️ [BrokerSignUp] Failed to get secure Stripe config, using fallback');
        const fallbackKey = process.env.REACT_APP_STRIPE_PUB_KEY as string;
        if (fallbackKey) {
          const stripe = loadStripe(fallbackKey);
          setStripePromise(stripe);
        } else {
          console.error('❌ [BrokerSignUp] No Stripe publishable key available');
          toast.error('Payment system unavailable. Please contact support.');
        }
      }
    };

    initializeStripe();
  }, []);  

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<BrokerInputs>();

  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [searchParams] = useSearchParams();
  const interval = searchParams.get("interval") || "month";
  const error = searchParams.get("error");
  const passwordValue = watch("password");

  const onSubmit: SubmitHandler<BrokerInputs> = async (data) => {
    setIsLoading(true);
    try {
      let idempotency_key = cookie?.stripeIdpKey;
    
      if (!idempotency_key) {
        idempotency_key = crypto?.randomUUID();
        setCookie('stripeIdpKey', idempotency_key, {
          path: '/',
          maxAge: 86400 // 24 hours
        });
      }

      const response = await axiosInstance.post("/auth/broker-registration", {
        plan_name: "BrokerPlan",
        idempotency_key,
        email: data.email,
        phone: data.phone,
        interval: interval,
        password: data.password,
        first_name: data.first_name,
        last_name: data.last_name,
        confirm_password: data.confirm_password,
        brokerage_name: data.brokerage_name,
        brokerage_license: data.brokerage_license,
        office_address: data.office_address,
        mls_number: data.mls_number,
      });

      if (!stripePromise) {
        throw new Error('Stripe is not initialized. Please refresh the page and try again.');
      }

      const stripe = await stripePromise;
      
      if (!stripe) {
        throw new Error('Stripe failed to initialize. Check your connection and try again.');
      }

      const result = await stripe.redirectToCheckout({
        sessionId: response.data.id,
      });
      if (result?.error) {
        console.log(result.error);
        throw new Error(result.error.message || 'Payment processing failed');
      }
    } catch (error: unknown) {
      let errorMessage = "Broker registration failed";
      if (error && typeof error === 'object' && 'response' in error && error.response && typeof error.response === 'object' && 'data' in error.response && error.response.data && typeof error.response.data === 'object' && 'message' in error.response.data) {
        errorMessage = String(error.response.data.message);
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error instanceof Error) {
        errorMessage = error.message;
      }
      toast.error(formatErrorForReact(errorMessage));
    }
    setIsLoading(false);
  };

  const navigate = useNavigate();

  useEffect(() => {
    if (cookie.isLoggedInKIQ) {
      navigate("/dashboard");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cookie.isLoggedInKIQ]);

  // Show toast for existing email error immediately on component mount
  useEffect(() => {
    if (error === "email_exists") {
      toast.error("This email is already registered. Please sign in instead.", {
        duration: 5000,
        position: "top-center",
        style: {
          background: "#FEE2E2",
          color: "#B91C1C",
          fontWeight: 500
        },
      });
      
      setTimeout(() => {
        const newParams = new URLSearchParams(searchParams.toString());
        newParams.delete("error");
        navigate(`/broker-signup?${newParams.toString()}`, { replace: true });
      }, 300);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Fragment>
      <div className="text-center mb-4">
        <Link to="/" className="d-flex flex-center text-decoration-none mb-3">
          <Logo
            text={false}
            width={100}
            className="fw-bolder fs-5 d-inline-block"
          />
        </Link>
        <div className="d-flex align-items-center justify-content-center mb-3">
          <div className="bg-primary rounded-circle p-3 me-3" style={{ background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)' }}>
            <FontAwesomeIcon icon={faBuilding} className="text-white" style={{ fontSize: '24px' }} />
          </div>
          <div>
            <h3 className="text-body-highlight mb-1" style={{ color: '#1f2937', fontWeight: '700' }}>
              Launch Your Brokerage on KairosIQ
            </h3>
            <p className="text-body-tertiary mb-0" style={{ fontSize: '14px', color: '#6b7280' }}>
              {interval === 'month' ? 'Monthly' : 'Annual'} subscription • Complete brokerage platform
            </p>
          </div>
        </div>
        <p className="text-body-tertiary" style={{ color: '#4b5563' }}>
          Manage your agents, track performance, and scale your business with AI-powered tools
        </p>
      </div>

      <Form onSubmit={handleSubmit(onSubmit)}>
        {/* Personal Information */}
        <div className="mb-4">
          <h5 className="text-body-highlight mb-3" style={{ color: '#374151', fontWeight: '600' }}>
            <FontAwesomeIcon icon={faCrown} className="me-2 text-primary" />
            Personal Information
          </h5>
          <Row className="g-3 mb-3">
            <Col lg={6}>
              <Form.Group className="text-start">
                <Form.Label htmlFor="first_name" style={{ color: '#374151', fontWeight: '500' }}>
                  First Name
                  <span className="text-danger"> *</span>
                </Form.Label>
                <Form.Control
                  id="first_name"
                  type="text"
                  placeholder="John"
                  style={{
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    padding: '12px 16px',
                    fontSize: '16px'
                  }}
                  {...register("first_name", {
                    required: true,
                    maxLength: 200,
                  })}
                />
                {errors.first_name?.type === "required" && (
                  <p role="alert" className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
                    First name is required
                  </p>
                )}
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="text-start">
                <Form.Label htmlFor="last_name" style={{ color: '#374151', fontWeight: '500' }}>
                  Last Name
                  <span className="text-danger"> *</span>
                </Form.Label>
                <Form.Control
                  id="last_name"
                  type="text"
                  placeholder="Smith"
                  style={{
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    padding: '12px 16px',
                    fontSize: '16px'
                  }}
                  {...register("last_name", {
                    required: true,
                    maxLength: 200,
                  })}
                />
                {errors.last_name?.type === "required" && (
                  <p role="alert" className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
                    Last name is required
                  </p>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col lg={6}>
              <Form.Group className="text-start">
                <Form.Label htmlFor="phone" style={{ color: '#374151', fontWeight: '500' }}>
                  Phone Number
                  <span className="text-danger"> *</span>
                </Form.Label>
                <Form.Control
                  id="phone"
                  type="tel"
                  inputMode="tel"
                  placeholder="(555) 123-4567"
                  style={{
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    padding: '12px 16px',
                    fontSize: '16px'
                  }}
                  {...register("phone", { required: true, maxLength: 100 })}
                />
                {errors.phone?.type === "required" && (
                  <p role="alert" className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
                    Phone number is required
                  </p>
                )}
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="text-start">
                <Form.Label htmlFor="email" style={{ color: '#374151', fontWeight: '500' }}>
                  Email Address
                  <span className="text-danger"> *</span>
                </Form.Label>
                <Form.Control
                  id="email"
                  type="email"
                  placeholder="john@realty.com"
                  style={{
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    padding: '12px 16px',
                    fontSize: '16px'
                  }}
                  {...register("email", { required: true, maxLength: 100 })}
                />
                {errors.email?.type === "required" && (
                  <p role="alert" className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
                    Email address is required
                  </p>
                )}
              </Form.Group>
            </Col>
          </Row>
        </div>

        {/* Brokerage Information */}
        <div className="mb-4">
          <h5 className="text-body-highlight mb-3" style={{ color: '#374151', fontWeight: '600' }}>
            <FontAwesomeIcon icon={faBuilding} className="me-2 text-primary" />
            Brokerage Information
          </h5>
          <Row className="g-3 mb-3">
            <Col lg={6}>
              <Form.Group className="text-start">
                <Form.Label htmlFor="brokerage_name" style={{ color: '#374151', fontWeight: '500' }}>
                  Brokerage Name
                  <span className="text-danger"> *</span>
                </Form.Label>
                <Form.Control
                  id="brokerage_name"
                  type="text"
                  placeholder="Smith Realty Group"
                  style={{
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    padding: '12px 16px',
                    fontSize: '16px'
                  }}
                  {...register("brokerage_name", {
                    required: true,
                    maxLength: 200,
                  })}
                />
                {errors.brokerage_name?.type === "required" && (
                  <p role="alert" className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
                    Brokerage name is required
                  </p>
                )}
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="text-start">
                <Form.Label htmlFor="brokerage_license" style={{ color: '#374151', fontWeight: '500' }}>
                  Brokerage License #
                  <span className="text-danger"> *</span>
                </Form.Label>
                <Form.Control
                  id="brokerage_license"
                  type="text"
                  placeholder="BR123456789"
                  style={{
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    padding: '12px 16px',
                    fontSize: '16px'
                  }}
                  {...register("brokerage_license", {
                    required: true,
                    maxLength: 100,
                  })}
                />
                {errors.brokerage_license?.type === "required" && (
                  <p role="alert" className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
                    Brokerage license number is required
                  </p>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col lg={8}>
              <Form.Group className="text-start">
                <Form.Label htmlFor="office_address" style={{ color: '#374151', fontWeight: '500' }}>
                  Office Address
                  <span className="text-danger"> *</span>
                </Form.Label>
                <Form.Control
                  id="office_address"
                  type="text"
                  placeholder="123 Main St, Phoenix, AZ 85001"
                  style={{
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    padding: '12px 16px',
                    fontSize: '16px'
                  }}
                  {...register("office_address", {
                    required: true,
                    maxLength: 300,
                  })}
                />
                {errors.office_address?.type === "required" && (
                  <p role="alert" className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
                    Office address is required
                  </p>
                )}
              </Form.Group>
            </Col>
            <Col lg={4}>
              <Form.Group className="text-start">
                <Form.Label htmlFor="mls_number" style={{ color: '#374151', fontWeight: '500' }}>
                  MLS Number (Optional)
                </Form.Label>
                <Form.Control
                  id="mls_number"
                  type="text"
                  placeholder="ARMLS123"
                  style={{
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    padding: '12px 16px',
                    fontSize: '16px'
                  }}
                  {...register("mls_number", {
                    maxLength: 50,
                  })}
                />
              </Form.Group>
            </Col>
          </Row>
        </div>

        {/* Password Section */}
        <div className="mb-4">
          <h5 className="text-body-highlight mb-3" style={{ color: '#374151', fontWeight: '600' }}>
            Account Security
          </h5>
          <Row className="g-3 mb-4">
            <Col lg={6}>
              <Form.Group className="position-relative text-start">
                <Form.Label htmlFor="password" style={{ color: '#374151', fontWeight: '500' }}>
                  Password<span className="text-danger"> *</span>
                </Form.Label>
                <Form.Control
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  className="pe-5"
                  style={{
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    padding: '12px 16px',
                    fontSize: '16px'
                  }}
                  autoComplete="new-password"
                  {...register("password", {
                    required: true,
                    minLength: {
                      value: 8,
                      message: "Password must be at least 8 characters long.",
                    },
                    maxLength: {
                      value: 40,
                      message: "Password cannot exceed 40 characters.",
                    },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="btn btn-link p-0 position-absolute end-0 translate-middle-y me-2"
                  style={{ textDecoration: "none", top: "72%" }}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
                {errors.password?.type === "required" && (
                  <p role="alert" className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
                    Password is required
                  </p>
                )}
                {errors.password && errors.password.message && (
                  <p role="alert" className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
                    {errors.password.message}
                  </p>
                )}
              </Form.Group>
            </Col>
            <Col lg={6}>
              <Form.Group className="position-relative text-start">
                <Form.Label htmlFor="confirmPassword" style={{ color: '#374151', fontWeight: '500' }}>
                  Confirm Password<span className="text-danger"> *</span>
                </Form.Label>
                <Form.Control
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="••••••••"
                  className="pe-5"
                  style={{
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    padding: '12px 16px',
                    fontSize: '16px'
                  }}
                  autoComplete="new-password"
                  {...register("confirm_password", {
                    required: "Confirm password is required",
                    validate: (value) =>
                      value === passwordValue || "Passwords do not match",
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword((prev) => !prev)}
                  className="btn btn-link p-0 position-absolute end-0 translate-middle-y me-2"
                  style={{ textDecoration: "none", top: "72%" }}
                >
                  <FontAwesomeIcon icon={showConfirmPassword ? faEyeSlash : faEye} />
                </button>
                {errors.confirm_password && (
                  <p role="alert" className="text-danger" style={{ fontSize: '14px', marginTop: '4px' }}>
                    {errors.confirm_password.message}
                  </p>
                )}
              </Form.Group>
            </Col>
          </Row>
        </div>

        {/* Enhanced Terms Notice */}
        <div className="mb-4 p-3" style={{
          background: 'rgba(99, 102, 241, 0.05)',
          borderRadius: '8px',
          border: '1px solid rgba(99, 102, 241, 0.1)'
        }}>
          <p className="mb-0" style={{ fontSize: '14px', color: '#4b5563', lineHeight: '1.5' }}>
            By continuing, you agree to KairosIQ's{" "}
            <Link 
              to="/terms-and-conditions" 
              target="_blank"
              style={{ color: '#6366f1', textDecoration: 'none' }}
            >
              Terms of Service
            </Link>
            {" "}and{" "}
            <Link 
              to="/privacy-policy" 
              target="_blank"
              style={{ color: '#6366f1', textDecoration: 'none' }}
            >
              Privacy Policy
            </Link>
            , including brokerage management features and commission tracking.
          </p>
        </div>

        {/* Continue to Payment Button */}
        <Button
          disabled={isLoading}
          variant="primary"
          type="submit"
          className="w-100 mt-2"
          style={{
            background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
            border: 'none',
            borderRadius: '8px',
            padding: '14px 24px',
            fontSize: '16px',
            fontWeight: '600',
            boxShadow: '0 4px 15px rgba(99, 102, 241, 0.3)'
          }}
        >
          {isLoading ? (
            <Spinner animation="border" role="status" size="sm" />
          ) : (
            <>
              <FontAwesomeIcon icon={faBuilding} className="me-2" />
              Launch Brokerage - ${interval === 'month' ? '1,000' : '850'}/mo
            </>
          )}
        </Button>

        {/* Sign In Link */}
        <div className="text-center mt-3">
          <span style={{ color: '#6b7280', fontSize: '14px' }}>
            Already have an account?{" "}
            <Link 
              to="/sign-in" 
              style={{ 
                color: '#6366f1', 
                textDecoration: 'none', 
                fontWeight: '600' 
              }}
            >
              Sign in here
            </Link>
          </span>
        </div>
      </Form>

      <div 
        className="mt-4 p-3 text-center"
        style={{
          background: 'rgba(99, 102, 241, 0.05)',
          borderRadius: '8px',
          border: '1px solid rgba(99, 102, 241, 0.1)'
        }}
      >
        <FontAwesomeIcon icon={faBuilding} className="text-primary me-2" />
        <span style={{ fontSize: '12px', color: '#4b5563' }}>
          Secure brokerage setup • Your data is encrypted • Add unlimited agents at $50/agent/month
        </span>
      </div>
    </Fragment>
  );
};

export default BrokerSignUpForm;